import React from "react";
import { Controller, RegisterOptions, useFormContext } from "react-hook-form";
import { makeStyles } from "@material-ui/core";
import DatePicker from "../../DatePicker";

type FormDatePickerProps = {
  name: string;
  label?: string;
  description?: string;
  rules?: RegisterOptions;
  size?: any;
  id?: string;
  placeholder?: string;
  height?: number;
  disabled?: boolean;
  variant?: "default" | "pl";
};

const useStyles = makeStyles(() => ({
  inputWrapper: {
    display: "flex",
    alignItems: "center",
    borderRadius: "8px",
    border: "1px solid #E2E8F0",
    backgroundColor: "white",
    transition: "all 0.2s ease",
    width: "100%",
    padding: "8px",
    boxSizing: "border-box" as const,
    "&:focus-within": {
      borderColor: "#003350",
      boxShadow: "0 0 0 3px rgba(0, 51, 80, 0.12)",
    },
  },
  plInputInner: {
    width: "100%",
    marginTop: "0 !important",
    marginBottom: "0 !important",
    "& .MuiInput-formControl": { marginTop: "0 !important" },
    "& .MuiInputBase-root:before": { borderBottom: "none !important" },
    "& .MuiInputBase-root:after": { borderBottom: "none !important" },
    "& .MuiInputBase-root:hover:not(.Mui-disabled):before": {
      borderBottom: "none !important",
    },
    "& .MuiInput-underline:before": { borderBottom: "none !important" },
    "& .MuiInput-underline:after": { borderBottom: "none !important" },
    "& .MuiInput-underline:hover:not(.Mui-disabled):before": { borderBottom: "none !important" },
    "& .MuiInputBase-input": {
      fontSize: "16px",
      color: "#334155",
      padding: "0 8px",
    },
    "& .MuiInputAdornment-root": {
      marginLeft: "0",
      marginRight: 0,
    },
    "& .MuiInputAdornment-root .MuiIconButton-root": {
      color: "#003350",
      padding: "4px",
      outline: "none",
    },
    "& .MuiInputAdornment-root .MuiIconButton-root:focus": {
      outline: "none",
      boxShadow: "none",
    },
    "& .MuiInputAdornment-root .MuiIconButton-root.Mui-focusVisible": {
      outline: "2px solid #003350",
      outlineOffset: "1px",
    },
  },
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    width: "100%",
  },
  label: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#364153",
    marginBottom: "4px",
  },
  requiredAsterisk: {
    color: "#EF4444",
  },
  helperText: {
    fontSize: "14px",
    color: "#EF4444",
    marginTop: "4px",
  },
}));

const FormDatePicker = ({
  name,
  label,
  description,
  rules,
  size = "small",
  id,
  placeholder,
  height,
  disabled = false,
  variant = "default",
}: FormDatePickerProps) => {
  const { control, trigger } = useFormContext();
  const classes = useStyles();

  // El formulario valida en modo "onBlur", pero elegir un día en el calendario cierra el popover
  // sin que el input llegue a perder el foco: sin este trigger, un vencimiento pasado recién se
  // señalaría al enviar. Se valida el campo apenas cambia para que el error salga en el momento.
  const handleChange = (field: { onChange: (date: unknown) => void }, date: unknown) => {
    field.onChange(date);
    void trigger(name);
  };

  const renderLabel = (labelText: string) => {
    const requiredMatch = labelText.match(/^(.*?)(\s*\*)$/);
    if (!requiredMatch) {
      return labelText;
    }

    return (
      <>
        {requiredMatch[1]}
        <span className={classes.requiredAsterisk}>{requiredMatch[2]}</span>
      </>
    );
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => (
        <div data-field-name={name} className={classes.container}>
          {label && <label className={classes.label}>{renderLabel(label)}</label>}
          {variant === "pl" ? (
            <div className={classes.inputWrapper}>
              <DatePicker
                id={id || name}
                name={name}
                value={field.value ?? null}
                label=""
                size="medium"
                placeholder={placeholder}
                registration={field}
                onchange={(date) => handleChange(field, date)}
                disabled={disabled}
                inputVariant="standard"
                margin="none"
                className={classes.plInputInner}
                primaryColor="#003350"
                disableUnderline={true}
              />
            </div>
          ) : (
            <DatePicker
              id={id || name}
              name={name}
              value={field.value ?? null}
              label=""
              size={size}
              placeholder={placeholder}
              height={height}
              registration={field}
              errors={fieldState.error?.message}
              onchange={(date) => handleChange(field, date)}
              disabled={disabled}
            />
          )}
          {variant === "pl" && fieldState.error?.message && (
            <span className={classes.helperText}>{fieldState.error.message}</span>
          )}
        </div>
      )}
    />
  );
};

export default FormDatePicker;
